'use client';

import {StepPayload} from '@blesk-frontend/api';
import {useCallback, useEffect, useRef, useState} from 'react';
import {TransformComponent, TransformWrapper} from 'react-zoom-pan-pinch';

import {Loader} from '../components';
import {Rects, TopBarControls} from './components';
import {scaleRect} from './libs';
import {ImageEditorProvider, useImageEditorContext} from './providers';
import {useRectStore} from './stores';
import {Rect} from './types';

export interface ImageEditorProps {
  step: StepPayload;
  onChange: (step: StepPayload) => void;
  onClose?: () => void;
}

const EditorInner = ({onChange, onClose = () => void 0}: Omit<ImageEditorProps, 'step'>) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [loading, setLoading] = useState(true);

  const {rects, setRects} = useRectStore();
  const {step} = useImageEditorContext(({step}) => ({
    step,
  }));

  const observeScaleRects = useCallback(
    (scale: number) => {
      const rects = step?.editor.rects.map((r) =>
        scaleRect({...r, active: false} as Rect, {x: scale, y: scale}),
      );
      setRects(rects || []);
    },
    [step, setRects],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (!imageRef.current) return;
      observeScaleRects(imageRef.current.clientWidth / imageRef.current.naturalWidth);
    });

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [imageRef, observeScaleRects]);

  const onLoad = () => {
    if (!imageRef.current) return;
    observeScaleRects(imageRef.current.clientWidth / imageRef.current.naturalWidth);
    setLoading(false);
  };

  return (
    <div className="relative flex h-full w-full flex-col" onContextMenu={(e) => e.preventDefault()}>
      <TopBarControls onSaveChanges={onChange} onClose={onClose} />

      {step?.src.edited ? (
        <TransformWrapper
          initialScale={1}
          minScale={0.5}
          maxScale={2.5}
          centerOnInit={true}
          limitToBounds={true}
          panning={{
            disabled: false,
            allowLeftClickPan: false,
          }}
        >
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
            }}
            contentStyle={{
              width: '100%',
            }}
          >
            <img
              ref={imageRef}
              data-editor-image="true"
              src={step.src.edited}
              onLoad={onLoad}
              alt=""
              className="h-full"
            />
            {!loading && <Rects rects={rects} boundingId={step.id} />}
          </TransformComponent>
        </TransformWrapper>
      ) : (
        <div className="flex size-full items-center justify-center text-text-secondary">
          <Loader size={32} />
        </div>
      )}
    </div>
  );
};

export const ImageEditor = ({step, ...props}: ImageEditorProps) => (
  <ImageEditorProvider step={step}>
    <EditorInner {...props} />
  </ImageEditorProvider>
);
